import { jobListSchema, type Job } from '@xphoto/shared';
import { apiFetch } from '../lib/http';
import { withSlotParam } from '../lib/activeSlot';
import { ApiError } from '../gallery/api';

/**
 * Client typé du centre de jobs pour la cloche de notifications.
 *
 * - `fetchJobs` : SEED via `GET /api/jobs` (authentifié → `apiFetch`, en-tête de
 *   slot). La réponse est RE-PARSÉE par le contrat partagé `jobListSchema`
 *   (« never trust the client » vaut aussi pour ses propres réponses).
 * - `jobStreamUrl` : URL du flux SSE `GET /api/jobs/stream`. `EventSource` NE
 *   pose PAS d'en-tête custom → le slot actif passe en `?slot=N` via
 *   `withSlotParam` (comme les vignettes/originaux). Les cookies (session
 *   same-origin) partent automatiquement.
 * - `cancelJob` : `POST /api/jobs/:id/cancel` (annulation coopérative, optionnel).
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON (ex. 429 du limiteur) : code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/** `GET /api/jobs?libraryId=<uuid>&limit=<n>` — SEED de la liste de jobs. */
export async function fetchJobs(
  libraryId: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<Job[]> {
  const sp = new URLSearchParams({ libraryId, limit: String(limit) });
  const res = await apiFetch(`/api/jobs?${sp.toString()}`, { signal: signal ?? null });
  return jobListSchema.parse(await readJson(res)).jobs;
}

/**
 * URL du flux SSE des jobs, avec le slot actif en `?slot=N` (EventSource ne peut
 * pas poser l'en-tête `X-Active-Slot`).
 */
export function jobStreamUrl(libraryId: string): string {
  return withSlotParam(`/api/jobs/stream?libraryId=${encodeURIComponent(libraryId)}`);
}

/** `POST /api/jobs/:id/cancel` — demande d'annulation coopérative (optionnel). */
export async function cancelJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${jobId}/cancel`, { method: 'POST' });
  await readJson(res);
}

/**
 * `POST /api/jobs/:id/retry` — ré-enfile un job ÉCHOUÉ avec les mêmes `params`
 * (audit B3). Le serveur tranche : 403 (lecture seule), 409 `JOB_NOT_RETRYABLE`
 * (type non rejouable) ou 409 `JOB_ALREADY_RUNNING` (garde anti-empilement). Les codes
 * remontent via `ApiError` pour un message ACTIONNABLE côté cloche.
 */
export async function retryJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${jobId}/retry`, { method: 'POST' });
  await readJson(res);
}
